/**
 * TanStack Query Hooks for Tag Operations
 *
 * Part of the Data Workspace OS feature set.
 * Provides type-safe access to tag CRUD and assignment operations with:
 * - Automatic caching and deduplication
 * - Background refetching
 * - Optimistic updates for mutations
 * - Query invalidation on changes
 *
 * @module features/organization/hooks/useTagsQueries
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { TagCreateRequest, TagUpdateRequest, TagWithCounts } from '@loader/shared-types';

import { useRpc, extractErrorMessage } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch all tags in the workspace
 *
 * Returns tags with version usage counts.
 * Sorted by name by default.
 */
export function useTagsQuery(
  options?: {
    enabled?: boolean;
    sortBy?: 'name' | 'created_at' | 'usage';
    sortDir?: 'asc' | 'desc';
  }
) {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.organization.tags.list({
      sortBy: options?.sortBy,
      sortDir: options?.sortDir,
    }),
    queryFn: async () => {
      const res = await rpc.api.private.organization.tags.$get({
        query: {
          sort_by: options?.sortBy,
          sort_dir: options?.sortDir,
        },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch tags');
      }

      const data = await res.json();
      return data.tags as TagWithCounts[];
    },
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch a single tag by ID
 *
 * Includes usage counts.
 */
export function useTagQuery(id: string | undefined, options?: { enabled?: boolean }) {
  const rpc = useRpc();

  return useQuery({
    queryKey: id
      ? queryKeys.organization.tags.detail(id)
      : queryKeys.organization.tags.detailDisabled,
    queryFn: async () => {
      if (!id) throw new Error('Tag ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.tags[':id'].$get({
        param: { id },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch tag');
      }

      const data = await res.json();
      return data.tag as TagWithCounts;
    },
    enabled: !!id && options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch tags assigned to a specific version
 */
export function useVersionTagsQuery(
  versionId: string | undefined,
  options?: { enabled?: boolean }
) {
  const rpc = useRpc();

  return useQuery({
    queryKey: versionId
      ? queryKeys.organization.tags.forVersion(versionId)
      : queryKeys.organization.tags.forVersionDisabled,
    queryFn: async () => {
      if (!versionId) throw new Error('Version ID is required');

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.tags.version[':versionId'].$get({
        param: { versionId },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch version tags');
      }

      const data = await res.json();
      return data.tags as TagWithCounts[];
    },
    enabled: !!versionId && options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new tag
 *
 * Automatically invalidates tag queries on success.
 */
export function useCreateTagMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TagCreateRequest) => {
      const res = await rpc.api.private.organization.tags.$post({
        json: data,
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to create tag'));
      }

      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.tags.all,
      });
    },
  });
}

/**
 * Update an existing tag
 *
 * Supports partial updates (name, color, description).
 */
export function useUpdateTagMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: TagUpdateRequest }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.tags[':id'].$patch({
        param: { id },
        json: data,
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to update tag'));
      }

      return res.json();
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.tags.detail(variables.id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.tags.all,
      });
    },
  });
}

/**
 * Delete a tag
 *
 * Removes tag from all assigned versions.
 */
export function useDeleteTagMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.tags[':id'].$delete({
        param: { id },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to delete tag'));
      }

      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.tags.all,
      });
      // Also invalidate versions as they display tags
      void queryClient.invalidateQueries({
        queryKey: queryKeys.versions.all,
      });
    },
  });
}

/**
 * Set tags for a version (replaces all existing tags)
 *
 * Supports optimistic updates when tableId is provided.
 */
export function useSetVersionTagsMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ versionId, tagIds }: {
      versionId: string;
      tagIds: string[];
      /** Optional: provide for optimistic updates in timeline */
      tableId?: string;
      /** Optional: provide tag details for optimistic updates */
      newTags?: Array<{ id: string; name: string; color: string | null }>;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.tags.version[':versionId'].$put({
        param: { versionId },
        json: { tag_ids: tagIds },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to set version tags'));
      }

      return res.json();
    },
    onMutate: async ({ versionId, tableId, newTags }) => {
      // Only do optimistic updates if tableId and newTags are provided
      if (!tableId || !newTags) return;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.tables.versions(tableId) });

      // Snapshot previous value
      const previousVersions = queryClient.getQueryData(queryKeys.tables.versions(tableId));

      // Optimistically update cache
      queryClient.setQueryData(
        queryKeys.tables.versions(tableId),
        (old: Array<{ id: string; tags?: Array<{ id: string; name: string; color: string | null }> }> | undefined) => {
          if (!old) return old;
          return old.map((v) => {
            if (v.id === versionId) {
              return { ...v, tags: newTags };
            }
            return v;
          });
        }
      );

      return { previousVersions, tableId };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.tableId && context?.previousVersions) {
        queryClient.setQueryData(
          queryKeys.tables.versions(context.tableId),
          context.previousVersions
        );
      }
    },
    onSettled: (_data, _err, variables, context) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.tags.forVersion(variables.versionId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.tags.all,
      });
      // Invalidate versions for consistency
      if (context?.tableId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.tables.versions(context.tableId),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.versions.all,
      });
    },
  });
}
